import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/axios";
import styles from "./OrderDetailPage.module.css";

const STATUS_STEPS = ["processing", "confirmed", "shipped", "delivered"];
const STATUS_BADGE = {
  processing: "badge-amber", confirmed: "badge-purple",
  shipped: "badge-gray", delivered: "badge-green", cancelled: "badge-red",
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (searchParams.get("payment") === "success") toast.success("Payment successful! Order confirmed.");
    if (searchParams.get("payment") === "cancelled") toast.error("Payment was cancelled.");
    api.get(`/orders/${id}`).then(r => setOrder(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;
  if (!order) return <div className="empty-state"><h3>Order not found</h3></div>;

  const stepIndex = STATUS_STEPS.indexOf(order.orderStatus);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Order #{order._id.slice(-8).toUpperCase()}</h1>
            <div className={styles.date}>{new Date(order.createdAt).toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}</div>
          </div>
          <span className={`badge ${STATUS_BADGE[order.orderStatus] || "badge-gray"}`} style={{fontSize:"14px",padding:"6px 14px"}}>
            {order.orderStatus}
          </span>
        </div>

        {order.orderStatus !== "cancelled" && (
          <div className={styles.tracker}>
            {STATUS_STEPS.map((step, i) => (
              <div key={step} className={`${styles.step} ${i <= stepIndex ? styles.done : ""}`}>
                <div className={styles.stepDot}>{i <= stepIndex ? "✓" : i + 1}</div>
                <div className={styles.stepLabel}>{step.charAt(0).toUpperCase() + step.slice(1)}</div>
                {i < STATUS_STEPS.length - 1 && <div className={`${styles.stepLine} ${i < stepIndex ? styles.doneLine : ""}`}/>}
              </div>
            ))}
          </div>
        )}

        <div className={styles.grid}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Items ordered</h3>
            {order.items.map((item, i) => (
              <div key={i} className={styles.item}>
                <div className={styles.itemImg}>
                  {item.image ? <img src={item.image} alt={item.name} /> : <span>◈</span>}
                </div>
                <div className={styles.itemInfo}>
                  <div className={styles.itemName}>{item.name}</div>
                  <div className={styles.itemQty}>Qty: {item.quantity}</div>
                </div>
                <div className={styles.itemPrice}>₹{(item.price * item.quantity).toLocaleString()}</div>
              </div>
            ))}
            <div className={styles.summary}>
              <div className={styles.summaryRow}><span>Items</span><span>₹{order.itemsPrice.toLocaleString()}</span></div>
              <div className={styles.summaryRow}><span>Shipping</span><span>{order.shippingPrice === 0 ? "Free" : `₹${order.shippingPrice}`}</span></div>
              <div className={`${styles.summaryRow} ${styles.summaryTotal}`}><span>Total</span><span>₹{order.totalPrice.toLocaleString()}</span></div>
            </div>
          </div>

          <div>
            <div className={styles.section} style={{marginBottom:"16px"}}>
              <h3 className={styles.sectionTitle}>Shipping address</h3>
              <div className={styles.address}>
                <div>{order.shippingAddress.street}</div>
                <div>{order.shippingAddress.city}, {order.shippingAddress.state}</div>
                <div>{order.shippingAddress.pincode}</div>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Payment</h3>
              <div className={styles.payRow}>
                <span>Status</span>
                <span className={`badge ${order.paymentInfo.status === "paid" ? "badge-green" : "badge-amber"}`}>
                  {order.paymentInfo.status}
                </span>
              </div>
              <div className={styles.payRow}><span>Method</span><span>Stripe</span></div>
              {order.paymentInfo.paidAt && (
                <div className={styles.payRow}>
                  <span>Paid at</span>
                  <span>{new Date(order.paymentInfo.paidAt).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
